/**
 * "For Agencies" — white-label landing page.
 * Distinct from the consumer Workflows hub: emphasizes white-label, multi-client,
 * deliver-under-your-brand. Reuses the cream + deep-purple visual system but with
 * an asymmetric two-column hero and credibility-first content.
 */
import { useNavigate } from "react-router-dom";
import { ArrowRight, Briefcase, Layers, Users, FileCheck, Repeat, ShieldCheck } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import "./workflows/workflows.css";

const WINS = [
  { icon: FileCheck, t: "White-labeled deliverables", d: "Every workflow output exports under your brand. Logo, fonts, colors — all yours." },
  { icon: Repeat, t: "10x faster client work", d: "Idea → finished package in minutes. Run 5 client workflows in parallel without burning your team out." },
  { icon: Users, t: "Multi-client workspace", d: "Switch brands, audiences, and projects in one click. No more juggling logins or templates." },
  { icon: Layers, t: "84+ tools, one platform", d: "Stop subscribing to 8 different SaaS tools. Everything your team needs, included." },
  { icon: ShieldCheck, t: "Team permissions + audit", d: "Granular roles for strategists, writers, designers. Full activity log for client transparency." },
  { icon: Briefcase, t: "Reseller-ready pricing", d: "Bulk seats, custom domain (white-label), priority support. Built for agencies that bill clients." },
];

const STEPS = [
  { n: "01", t: "Onboard your team", d: "Invite strategists, writers, designers. Set roles and per-client access." },
  { n: "02", t: "Add your brand kit", d: "Upload logo + fonts + colors once. Every deliverable inherits your brand." },
  { n: "03", t: "Run workflows per client", d: "Pick a goal, hand-off auto-runs, finished package lands under your brand." },
  { n: "04", t: "Bill on outcomes", d: "Stop billing hours. Bill on what you actually deliver." },
];

export default function ForAgenciesPage() {
  const navigate = useNavigate();
  return (
    <>
      <Header />
      <div className="wf-hub" data-testid="for-agencies-page">
        {/* HERO */}
        <div className="wf-hub-inner" style={{ paddingBottom: 40 }}>
          <span className="wf-eyebrow" data-testid="agencies-eyebrow">For agencies & freelancers</span>
          <h1 className="wf-hub-title" data-testid="agencies-headline">
            Deliver client work <em>10× faster.</em><br />Under your brand.
          </h1>
          <p className="wf-hub-sub" data-testid="agencies-subhead">
            Run every client engagement on MarkeeMix workflows. Your strategists pick a goal, the platform
            runs 8 tools in sequence, and the finished package lands under your logo. No swivel-chairing
            between 8 different SaaS tools. No more billing hours you can't justify.
          </p>
          <div style={{ marginTop: 36, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button className="wf-btn" onClick={() => navigate("/pricing")} data-testid="agencies-primary-cta">
              <Briefcase size={16}/> See agency pricing
            </button>
            <button className="wf-btn wf-btn--ghost" onClick={() => navigate("/workflows")} data-testid="agencies-secondary-cta">
              Try a workflow free <ArrowRight size={14}/>
            </button>
          </div>
        </div>

        {/* WINS */}
        <div className="wf-hub-inner" style={{ paddingTop: 0 }}>
          <h2 style={{ fontSize: "1.85rem", letterSpacing: "-0.02em", fontWeight: 700, marginBottom: 32 }} data-testid="agencies-wins-title">
            Why agencies pick MarkeeMix.
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 22 }}>
            {WINS.map((w) => {
              const Icon = w.icon;
              return (
                <div key={w.t} className="wf-card" data-testid={`agencies-win-${w.t.replace(/\s+/g, '-').toLowerCase()}`} style={{ cursor: "default" }}>
                  <div className="wf-card-icon"><Icon /></div>
                  <h3 className="wf-card-title">{w.t}</h3>
                  <p className="wf-card-tagline" style={{ marginBottom: 0 }}>{w.d}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* HOW IT WORKS — agency flow */}
        <div className="wf-hub-inner" style={{ paddingTop: 24 }}>
          <h2 style={{ fontSize: "1.85rem", letterSpacing: "-0.02em", fontWeight: 700, marginBottom: 32 }} data-testid="agencies-how-title">
            Your agency on MarkeeMix.
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 22 }}>
            {STEPS.map((s) => (
              <div key={s.n} className="wf-card" style={{ cursor: "default" }} data-testid={`agencies-step-${s.n}`}>
                <div style={{ fontFamily: "ui-monospace, monospace", color: "#6d28d9", fontSize: "0.85rem", letterSpacing: "0.1em", marginBottom: 10 }}>{s.n}</div>
                <h3 className="wf-card-title">{s.t}</h3>
                <p className="wf-card-tagline" style={{ marginBottom: 0 }}>{s.d}</p>
              </div>
            ))}
          </div>
        </div>

        {/* FINAL CTA */}
        <div className="wf-hub-inner" style={{ paddingTop: 24, paddingBottom: 80 }}>
          <div className="wf-card" style={{ cursor: "default", padding: "44px 32px", textAlign: "center" }} data-testid="agencies-final-cta">
            <h2 style={{ fontSize: "2rem", letterSpacing: "-0.02em", fontWeight: 700, margin: "0 0 14px", maxWidth: "22ch", marginInline: "auto" }}>
              Stop renting 8 SaaS tools. Own the workflow.
            </h2>
            <p className="wf-card-tagline" style={{ maxWidth: "55ch", marginInline: "auto", marginBottom: 22 }}>
              Talk to our team about agency seats, white-label setup, and reseller pricing.
            </p>
            <button className="wf-btn" onClick={() => navigate("/pricing")} data-testid="agencies-final-cta-button" style={{ padding: "14px 28px", fontSize: "1rem" }}>
              <Briefcase size={16}/> Book a 15-min demo
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
